import { useState, useEffect, useCallback } from 'react';
import { homeApi, HomeDashboardData } from '@/api/home';
import { useAuth } from '@/contexts/AuthContext';
import { refineErrorMessage } from '@/utils/errorMessages';

interface UseHomeDashboardResult {
  data: HomeDashboardData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useHomeDashboard(): UseHomeDashboardResult {
  const { isAuthenticated, isTokenReady } = useAuth();
  const [data, setData] = useState<HomeDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = useCallback(async () => {
    // Wait for BOTH authentication AND token to be ready
    // This prevents the race condition where setUser triggers this
    // before localStorage.setItem has completed
    if (!isAuthenticated || !isTokenReady) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await homeApi.getDashboard();
      setData(response);
    } catch (err: any) {
      console.error('[useHomeDashboard] Error:', err);
      setError(refineErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, isTokenReady]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchDashboard,
  };
}
